// @ts-check
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { useNavigate, useParams, Link } from "react-router";
import { ToastContainer } from "react-toastify";

import { getTemplatesByDesign } from "@/services/templateServices/templateAction";
import { getPageById, updatePageContent } from "@/services/pageServices/pageAction";
import NotificationAlertOptions from "@/layouts/Alerts";

import UpdatePageHeaderFooterView from "./UpdatePageHeaderFooterView";
import { toPageContentEntry } from "../pageContentEntry";
import { asCaught } from "@/services/caughtError";

/**
 * `setPage` updater that replaces one column's entries with the variables just
 * selected for it.
 *
 * SCRUM-1514: the entries no longer carry the column, so the half to keep is
 * identified from the template instead — "everything this column does not
 * declare". That is deliberately the complement rather than "everything the
 * OTHER column declares": an entry the template declares nowhere (a variable
 * dropped from the template, with authored content still on the page) used to
 * survive here on its stored tag, and matching on the other column's names would
 * have deleted it.
 *
 * Module scope, so the `useCallback`s below genuinely have no dependencies.
 *
 * `list` is the template variables now selected for the column.
 */
const replaceColumn = (list: any[], columnKey: "ContentRequired" | "ContentOptional") => (prev) => { // any: template variables picked for the column; no client type for a template variable yet
  const columnNames = new Set(
    (prev.page_template?.content_variables?.[columnKey] || []).map(
      (v) => v.variable
    )
  );
  return {
    ...prev,
    page_content: (prev.page_content || [])
      .filter((c) => !columnNames.has(c.variable))
      .concat(list.map((ctn) => toPageContentEntry(ctn, ctn.value))),
  };
};

export default function UpdatePageHeaderFooterContainer() {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  // Redux state
  const CurrentSite = useAppSelector((s) => s.sites.site);
  // SCRUM-1809: the page this screen edits, fetched by id rather than looked up
  // in the site document's paged `pages[]`.
  const pageById = useAppSelector((s) => s.pages.page_to_update);
  const templatesByDesign = useAppSelector((s) => s.templates.templates_by_design_site || []);

  // Local state
  const [page, setPage] = useState<any>({}); // any: no client type for the page document yet; the form edits its fields by name

  // SCRUM-1885: the post-save redirect is an imperative navigate, not render
  // state. A `redirect` state rendered as <Navigate> stayed set on an instance
  // that survived a quick browser Back, and that instance then rendered nothing.
  // The timer is cleared on unmount, so an editor who leaves during the toast is
  // not pulled back to the list.
  const navigate = useNavigate();
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  // templates: dropdown-friendly
  const selectTemplates = useMemo(
    () => templatesByDesign.map((temp) => ({ label: temp.name, value: temp })),
    [templatesByDesign]
  );

  // SCRUM-1809: fetch the page by id when the store does not already have it, so
  // this screen no longer depends on the site document having paged in the batch
  // that holds it. Same route `UpdatePageContent` takes (SCRUM-1514), and the
  // same "only when it is missing" shape as `useDropdownOptions.js`.
  //
  // Conditional on purpose. An unconditional fetch would fire on every mount,
  // including the overwhelmingly common case where the page is already in
  // `required_pages` — a request per screen open for data the store holds, and
  // a behaviour change for every existing caller. The symptom this slice removes
  // is the page being ABSENT, so that is the only case worth a request.
  // SCRUM-1812: only `required_pages` — the site response no longer carries
  // `pages`, so a non-chrome page is always fetched by id.
  const inStore =
    CurrentSite?.required_pages?.some((p) => String(p._id) === String(id));
  useEffect(() => {
    if (id && !inStore && String(pageById?._id || "") !== String(id)) {
      dispatch(getPageById(id));
    }
  }, [dispatch, id, inStore, pageById?._id]);

  // Find page once CurrentSite is present (from required_pages / pages)
  useEffect(() => {
    // SCRUM-1809: `CurrentSite` is no longer required to find the page — the
    // fetch above answers on its own. Only the id is.
    if (!id) return;

    // SCRUM-1528: `required_pages` FIRST. The two lists carry the same documents
    // populated differently — `controllers/site.js` populates `required_pages`
    // with the whole `page_template` (:119-124, no select) and `pages` with
    // `'name type'` (:157). This screen needs `page_template.content_variables`:
    // UpdatePageHeaderFooterView gates the entire "Template Variables
    // Configuration" block on it (:185), so a narrowly populated page renders a
    // template name and nothing else.
    //
    // Searching `pages` first was harmless while that list came from the
    // denormalised `pages[]` id array on the Site, which does not contain the required pages —
    // the lookup missed and the fully populated entry won. SCRUM-1454
    // (`8f5003e7`) moved it to `Page.find({site})`, which does contain them, and
    // the narrow entry started winning: the nav/footer editor went blank.
    //
    // A page that is NOT a required page is unaffected: it is absent from
    // `required_pages`, so it falls through to exactly the `pages` lookup this
    // screen did before.
    //
    // SCRUM-1809: `pageById` FIRST, and the two store lists only as a fallback.
    // `getPageById` populates `page_template` with `select: '-settings_variables'`
    // (`server/controllers/page.js`), i.e. INCLUDING `content_variables` — which
    // is the whole point of the SCRUM-1528 ordering above, so reading it first
    // satisfies that requirement rather than working around it. The fallback is
    // kept so the screen still renders from a warm store while the fetch is in
    // flight, and so this change cannot regress a page the route has not
    // answered for yet.
    // Use String() to ensure proper comparison (id from useParams is string, _id might be ObjectId)
    const fetched =
      String(pageById?._id || "") === String(id || "") ? pageById : undefined;
    const found =
      fetched ||
      CurrentSite?.required_pages?.find((p) => String(p._id) === String(id));

    // SCRUM-1809: guarded. This line was unguarded and unreachable with a falsy
    // `found`, because the effect used to bail out unless `CurrentSite` was
    // loaded, by which time the lookup succeeded. Now the effect also runs
    // before the fetch resolves, so an unguarded call would write `undefined`
    // over the initial `{}` and the view would render a blank form for one
    // tick. The found case is unchanged: both calls still run, in this order.
    if (found) setPage(found)
    if (found) {
      setPage((prev) => {
        // keep existing edits if already present
        const initial = {
          ...found,
          page_template: found.page_template || {},
          page_content: found.page_content || [],
        };
        return Object.keys(prev || {}).length ? prev : initial;
      });
    }
  }, [CurrentSite, id, pageById]);

  // On mount, load templates for current design
  useEffect(() => {
    if (CurrentSite?.design?._id) {
      dispatch(getTemplatesByDesign(CurrentSite.design._id));
    }
  }, [dispatch, CurrentSite?.design?._id]);

  // Handlers
  const handleTemplateChange = useCallback((opt) => {
    const value = opt ? opt.value : {};
    if (value?.design) {
      // SCRUM-1514: the variable and its value, never the template's declaration
      // — UpdatePageHeaderFooterView derives that back for rendering.
      const required = (value.content_variables?.ContentRequired || []).map((v) =>
        toPageContentEntry(v, v.value)
      );
      const optional = (value.content_variables?.ContentOptional || []).map((v) =>
        toPageContentEntry(v, v.value)
      );
      setPage((prev) => ({
        ...prev,
        page_template: value,
        page_content: [...required, ...optional],
      }));
    } else {
      setPage((prev) => ({
        ...prev,
        page_template: value,
        page_content: [],
      }));
    }
  }, []);

  const handleContentRequiredChange = useCallback((list) => {
    setPage(replaceColumn(list, "ContentRequired"));
  }, []);

  const handleContentOptionalChange = useCallback((list) => {
    setPage(replaceColumn(list, "ContentOptional"));
  }, []);

  const handleVariableValueChange = useCallback((e, action) => {
    setPage((prev) => {
      const content = [...(prev.page_content || [])];

      // input (string/number)
      if (e?.target) {
        const name = e.target.name;
        const idx = content.findIndex((cv) => cv.variable === name);
        if (idx >= 0) content[idx] = { ...content[idx], value: e.target.value };
      }

      // boolean select
      if (e?.label && action?.name) {
        const idx = content.findIndex((cv) => cv.variable === action.name);
        if (idx >= 0) content[idx] = { ...content[idx], value: e.value };
      }

      return { ...prev, page_content: content };
    });
  }, []);

  const handleLinkInputRequired = useCallback((event, targetVariableName) => {
    const key = event.target.name; // "name" | "link"
    const val = event.target.value;
    setPage((prev) => ({
      ...prev,
      page_content: (prev.page_content || []).map((cv) =>
        cv.variable === targetVariableName
          ? { ...cv, value: { ...(cv.value || {}), [key]: val } }
          : cv
      ),
    }));
  }, []);

  // SCRUM-853 D1(a): merge an embed-reported variable back into page_content
  // by variable name (same contract as UpdatePageContainer) so embed edits
  // persist through updatePageContent.
  //
  // SCRUM-1508: take the VALUE only. The view hands the embeds a copy whose
  // declarations are resolved from the template, so writing the reported entry
  // whole would persist that resolution on the next save. Behaviour-preserving
  // on its own — the embeds only ever build `{ ...v, value: next }` — and it is
  // what keeps the stored copy out of this slice.
  const handleVariableChange = useCallback((updatedVariable) => {
    setPage((prev) => ({
      ...prev,
      page_content: prev.page_content?.map((c) =>
        c.variable === updatedVariable.variable
          ? { ...c, value: updatedVariable.value }
          : c
      ),
    }));
  }, []);

  // SCRUM-853 D1(a): the Optional sitemap side-channel reports a
  // {target:{name,value}} envelope — merge the value by variable name.
  const handleOptionalEnvelopeChange = useCallback((e) => {
    const { name, value } = e.target;
    setPage((prev) => ({
      ...prev,
      page_content: (prev.page_content || []).map((cv) =>
        cv.variable === name ? { ...cv, value } : cv
      ),
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        await dispatch(updatePageContent(page));
        NotificationAlertOptions("success", "Updated Successfully!");
        redirectTimer.current = setTimeout(() => navigate("/admin/display-pages"), 1000);
      } catch (err) {
        NotificationAlertOptions("error", asCaught(err)?.message || "Update failed");
      }
    },
    [dispatch, page, navigate]
  );

  return (
    <>
      <ToastContainer />
      <UpdatePageHeaderFooterView
        CurrentSite={CurrentSite}
        page={page}
        selectTemplates={selectTemplates}
        onTemplateChange={handleTemplateChange}
        onContentRequiredChange={handleContentRequiredChange}
        onContentOptionalChange={handleContentOptionalChange}
        onVariableValueChange={handleVariableValueChange}
        onLinkVarChange={handleLinkInputRequired}
        onVariableChange={handleVariableChange}
        onChangeContentOptional={handleOptionalEnvelopeChange}
        onSubmit={handleSubmit}
        LinkComponent={Link}
      />
    </>
  );
}
